// Phase JA.5 — Daily Acca AnnouncementCard.
//
// Reads the latest daily_acca_announcement row (anon, last 7d).
// Composes Eyebrow / Stat / HairlineRow / Pill / Badge / Countdown
// primitives only. Renders empty-state Countdown to 23:45 WAT when
// today's row hasn't published yet. Never fabricates copy.

import { useMemo } from "react";
import { Badge, Countdown, Eyebrow, HairlineRow, Pill, Stat } from "../super-acca";
import {
  useDailyAccaAnnouncement,
  type DailyAccaAnnouncementRow,
} from "../../free-tier/useDailyAccaAnnouncement.ts";

/** 23:45 WAT (UTC+1) → 22:45 UTC. Roll forward if past. */
export function nextAnnouncementISO(now: Date = new Date()): string {
  const target = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 22, 45, 0, 0),
  );
  if (target.getTime() <= now.getTime()) {
    target.setUTCDate(target.getUTCDate() + 1);
  }
  return target.toISOString();
}

export function todayWatDateISO(now: Date = new Date()): string {
  const wat = new Date(now.getTime() + 60 * 60 * 1000);
  return wat.toISOString().slice(0, 10);
}

export interface AnnouncementCardProps {
  /** Optional override for tests. */
  announcement?: DailyAccaAnnouncementRow | null;
  loading?: boolean;
  nowISO?: string;
}

export function AnnouncementCardView({
  announcement,
  loading,
  nowISO,
}: AnnouncementCardProps): JSX.Element {
  const now = nowISO ? new Date(nowISO) : new Date();
  const target = useMemo(() => nextAnnouncementISO(now), [nowISO]);
  const today = todayWatDateISO(now);
  const haveToday = announcement !== null && announcement !== undefined && announcement.day === today;

  return (
    <section
      aria-label="Daily Acca Announcement"
      data-testid="announcement-card"
      style={{
        margin: "0 auto",
        maxWidth: 1180,
        padding: "12px 48px 32px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 14,
          marginBottom: 12,
          flexWrap: "wrap",
        }}
      >
        <Eyebrow>Today's Announcement</Eyebrow>
        {loading ? <Badge variant="ghost">SYNC</Badge> : null}
        {announcement && !haveToday ? <Pill color="var(--sa-fg-muted)">YESTERDAY</Pill> : null}
      </div>

      {!announcement ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 18,
            flexWrap: "wrap",
            padding: "14px",
            border: "1px solid var(--sa-hairline)",
            borderRadius: 4,
          }}
        >
          <span
            style={{
              fontFamily: "var(--sa-font-body)",
              fontSize: 13,
              color: "var(--sa-fg-secondary)",
            }}
          >
            Today's announcement publishes at 23:45 WAT.
          </span>
          <span style={{ flex: 1 }} />
          <span
            style={{
              fontFamily: "var(--sa-font-mono)",
              fontSize: 10,
              letterSpacing: "0.22em",
              color: "var(--sa-fg-muted)",
              textTransform: "uppercase",
            }}
          >
            Next announcement
          </span>
          <Countdown targetISO={target} compact />
        </div>
      ) : (
        <div
          style={{
            border: "1px solid var(--sa-hairline)",
            borderRadius: 4,
            padding: "16px 16px 0",
          }}
        >
          <div
            style={{
              fontFamily: "var(--sa-font-display, var(--sa-font-body))",
              fontSize: 20,
              lineHeight: 1.25,
              color: "var(--sa-fg-primary)",
              marginBottom: 8,
            }}
          >
            {announcement.headline}
          </div>
          <div
            style={{
              fontFamily: "var(--sa-font-body)",
              fontSize: 14,
              color: "var(--sa-fg-secondary)",
              lineHeight: 1.55,
              marginBottom: 14,
            }}
          >
            {announcement.body}
          </div>
          <HairlineRow first columns="repeat(4, minmax(120px, 1fr))" padY={14}>
            <Stat label="Published" value={String(announcement.signals_published)} sub="signals" />
            <Stat label="Won" value={String(announcement.signals_won)} accent={announcement.signals_won > 0} sub="settled" />
            <Stat label="Lost" value={String(announcement.signals_lost)} sub="settled" />
            <Stat label="Void" value={String(announcement.signals_void)} sub="unresolved" />
          </HairlineRow>
          {announcement.image_url ? (
            <div style={{ padding: "12px 0" }}>
              <img
                src={announcement.image_url}
                alt={announcement.headline}
                style={{ width: "100%", maxWidth: 600, borderRadius: 4, display: "block" }}
              />
            </div>
          ) : null}
        </div>
      )}
    </section>
  );
}

export function AnnouncementCard(): JSX.Element {
  const { announcement, loading } = useDailyAccaAnnouncement();
  return <AnnouncementCardView announcement={announcement} loading={loading} />;
}
